// Passkey-Helper. Etappe A: Registration. Etappe B: Login.
//
// Die Backend-Antworten von register/begin und login/begin sind die
// JSON-Form der WebAuthn-Optionen (Felder challenge, user.id,
// excludeCredentials[*].id, allowCredentials[*].id sind base64url-
// codierte Strings). Vor dem navigator.credentials.{create,get}()-
// Aufruf konvertieren wir sie in ArrayBuffer und nach dem Erfolg
// wieder zurueck nach base64url.

import { postJSON } from "./api";
import { frischeBestaetigung } from "./authSettings.svelte";
import {
  decodeOptions,
  decodeRequestOptions,
  encodeAssertion,
  encodeAttestation,
  type PublicKeyOptionsEnvelope,
  type RequestOptionsEnvelope,
} from "./webauthn-json";

export interface PasskeyCredentialView {
  id: number;
  name: string;
  created_at: string;
  last_used_at?: string | null;
  transports?: string[];
}

export async function listPasskeys(): Promise<PasskeyCredentialView[]> {
  const r = await fetch("/v2-api/auth/passkey/credentials", {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`Liste fehlgeschlagen (${r.status})`);
  return (await r.json()) as PasskeyCredentialView[];
}

// registerPasskey laeuft komplett in dieser Funktion: begin holt die
// Options, navigator.credentials.create wartet auf den Authenticator,
// finish persistiert das Ergebnis. Optionaler name landet als
// Default-Anzeigename; AAGUID-basierte Defaults setzt das Backend.
export async function registerPasskey(
  name?: string,
): Promise<{ id: number; name: string }> {
  if (!window.PublicKeyCredential) {
    throw new Error("Browser unterstuetzt WebAuthn nicht");
  }
  const optEnv = await postJSON<PublicKeyOptionsEnvelope>(
    "/v2-api/auth/passkey/register/begin",
  );
  const opts = decodeOptions(optEnv.publicKey);

  const credential = (await navigator.credentials.create({
    publicKey: opts,
  })) as PublicKeyCredential | null;
  if (!credential) {
    throw new Error("Authenticator hat keine Antwort geliefert");
  }

  const body = encodeAttestation(credential);
  const url =
    "/v2-api/auth/passkey/register/finish" +
    (name ? `?name=${encodeURIComponent(name)}` : "");
  return await postJSON<{ id: number; name: string }>(url, body);
}

export async function renamePasskey(id: number, name: string): Promise<void> {
  await postJSON<{ ok: true }>(
    `/v2-api/auth/passkey/credentials/${id}/rename`,
    { name },
  );
}

/** Entziehen verlangt einen Tipp auf einen (anderen oder denselben)
 *  Passkey -- Sitzung und PIN genügen nicht (Befund 11). */
export async function revokePasskey(id: number): Promise<void> {
  const assertion = await frischeBestaetigung();
  await postJSON<{ ok: true }>(
    `/v2-api/auth/passkey/credentials/${id}/revoke`,
    { assertion },
  );
}

// ---------- Login (Etappe B) ----------

export interface PasskeyLoginResult {
  ok: true;
  username: string;
  display_name: string;
}

// loginWithPasskey laeuft den vollstaendigen Discoverable-Login.
// mediation: "optional" triggert die Browser-UI sofort, "conditional"
// haengt sich an einen autocomplete-Input (Passkey wird beim Fokus
// vorgeschlagen) und ist fuer den Auto-Suggest-Pfad gedacht.
export async function loginWithPasskey(
  mediation: CredentialMediationRequirement = "optional",
  signal?: AbortSignal,
): Promise<PasskeyLoginResult> {
  if (!window.PublicKeyCredential) {
    throw new Error("Browser unterstuetzt WebAuthn nicht");
  }
  const optEnv = await postJSON<RequestOptionsEnvelope>(
    "/v2-api/auth/passkey/login/begin",
  );
  const opts = decodeRequestOptions(optEnv.publicKey);

  const credential = (await navigator.credentials.get({
    publicKey: opts,
    mediation,
    signal,
  })) as PublicKeyCredential | null;
  if (!credential) {
    throw new Error("Authenticator hat keine Antwort geliefert");
  }

  return await postJSON<PasskeyLoginResult>(
    "/v2-api/auth/passkey/login/finish",
    encodeAssertion(credential),
  );
}

// isConditionalMediationAvailable prueft ob der Browser die "Conditional UI"
// (autocomplete="webauthn") unterstuetzt. Caller sollten das Ergebnis nur
// nutzen, um die Hint-UI sichtbar zu machen -- nicht den Login darauf zu
// blockieren.
export async function isConditionalMediationAvailable(): Promise<boolean> {
  try {
    const cls = window.PublicKeyCredential as
      | (typeof PublicKeyCredential & {
          isConditionalMediationAvailable?: () => Promise<boolean>;
        })
      | undefined;
    if (!cls?.isConditionalMediationAvailable) return false;
    return await cls.isConditionalMediationAvailable();
  } catch {
    return false;
  }
}
